import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import useVendorOrderDetails from '../../hooks/useVendorOrderDetails';
import '/src/assets/css/orderdetails/vendorOrderDetails.css';
import axios from 'axios';
import {
  FaTruck,
  FaUserCircle,
  FaCheckCircle,
  FaClock,
  FaBox,
  FaMoneyBillWave,
  FaShoppingCart,
  FaShippingFast,
  FaPercent,
  FaReceipt,
  FaKey,
  FaShieldAlt,
  FaSpinner,
  FaExclamationTriangle,
  FaPhone,
  FaMapMarkerAlt,
  FaEnvelope,
  FaCalendarAlt
} from 'react-icons/fa';
import SafeImage from '../../components/common/SafeImage';

export default function VendorOrderDetails() {
  const { id } = useParams();
  const { order, isLoading, error } = useVendorOrderDetails(id);

  const [otp, setOtp] = useState('');
  const [otpStatus, setOtpStatus] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

// Only set OTP success if order is ongoing AND otp_verified is true
useEffect(() => {
  if (order?.otp_verified &&
      otpStatus?.status !== 'success' &&
      ['ongoing', 'processing', 'confirmed', 'shipped', 'out_for_delivery'].includes(order.status.toLowerCase())
  ) {
    setOtpStatus({ status: 'success', message: 'Driver Verified Successfully ✅' });
  }
}, [order, otpStatus]);

  const statusIcons = {
    pending: <FaClock className="v_od-status-icon" />,
    confirmed: <FaCheckCircle className="v_od-status-icon" />,
    processing: <FaSpinner className="v_od-status-icon v_od-spinning" />,
    ongoing: <FaTruck className="v_od-status-icon" />,
    completed: <FaCheckCircle className="v_od-status-icon" />,
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      processing: 'Processing',
      ongoing: 'Ongoing',
      completed: 'Completed',

    };
    return statusMap[status.toLowerCase()] || status;
  };

  if (isLoading) return (
    <div className="v_od-loading">
      <div className="v_od-loading-spinner"></div>
      <p>Loading order details...</p>
    </div>
  );

  if (error || !order) return (
    <div className="v_od-error">
      <div className="v_od-error-icon">
        <FaExclamationTriangle />
      </div>
      <h3>Order Not Found</h3>
      <p>The requested order could not be loaded.</p>
    </div>
  );

  const currency = order.currency_symbol || '₹';

  // Hide OTP input if already verified
  // Only show OTP input if order is ongoing AND driver exists AND OTP not verified
  const shouldShowOTP = order.driver?.name &&
                        ['ongoing', 'processing', 'confirmed', 'shipped', 'out_for_delivery'].includes(order.status.toLowerCase()) &&
                        otpStatus?.status !== 'success';



  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpStatus({ status: 'error', message: 'Enter a valid 6-digit OTP' });
      return;
    }

    setIsVerifying(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL || "http://localhost:8000"}/api/vendor/verify-driver-otp`,
        {
          order_id: order.order_id,
          otp
        },
        { withCredentials: true }
      );

      if (response.data.status === 'success') {
        setOtpStatus({ status: 'success', message: response.data.message });
      }
    } catch (err) {
      setOtpStatus({ 
        status: 'error', 
        message: err.response?.data?.message || 'PIN verification failed' 
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="v_od-container">
      {/* Header Section */}
      <div className="v_od-header">
        <div className="v_od-header-content">
          <h2 className="v_od-title">
            <FaShoppingCart className="v_od-title-icon" />
            Order #{order.order_code || order.order_id}
          </h2>
          <div className="v_od-meta">
            <span className="v_od-date">
              <FaCalendarAlt className="v_od-meta-icon" />
              Placed on: {new Date(order.ordered_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </div>
        <span className={`v_od-status v_od-status-${order.status.toLowerCase()}`}>
          {statusIcons[order.status.toLowerCase()] || <FaClock />}
          {getStatusText(order.status)}
        </span>
      </div>

      <div className="v_od-content">
        {/* Left Column */}
        <div className="v_od-left-column">
          {/* Driver Information */}
          <div className="v_od-card v_od-driver-card">
            <div className="v_od-card-header">
              <FaTruck className="v_od-card-icon" />
              <h3>Driver Information</h3>
            </div>
            <div className="v_od-card-body">
              {order.driver?.name ? (
                <>
                  <div className="v_od-driver-info">
                    <FaUserCircle className="v_od-driver-avatar" />
                    <div className="v_od-driver-details">
                      <h4>{order.driver.name}</h4>
                      {order.driver.phone && (
                        <p className="v_od-driver-contact">
                          <FaPhone className="v_od-driver-icon" />
                          {order.driver.phone}
                        </p>
                      )}
                      {order.driver.vehicle_number && (
                        <p className="v_od-driver-vehicle">
                          <FaTruck className="v_od-driver-icon" />
                          Vehicle: {order.driver.vehicle_number}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Delivery Status for Ongoing Orders */}
                  {['processing', 'confirmed', 'shipped', 'out_for_delivery'].includes(order.status.toLowerCase()) && (
                    <div className="v_od-delivery-status">
                      <FaShippingFast className="v_od-delivery-icon" />
                      <div className="v_od-delivery-info">
                        <p className="v_od-delivery-title">Delivery Status</p>
                        <p className="v_od-delivery-desc">Driver is on the way to deliver</p>
                        <p className="v_od-delivery-time">Expected delivery: Within 30 minutes</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="v_od-driver-unassigned">
                  <FaTruck className="v_od-no-driver-icon" />
                  <p>Driver Not Assigned Yet</p>
                  <p><FaSpinner className="v_od-status-icon v_od-spinning" /> </p>
                  <span className="v_od-waiting-text"> Waiting for driver assignment... </span>
                </div>
              )}
            </div>
          </div>

{/* Order Items */}
<div className="v_od-card v_od-items-card">
  <div className="v_od-card-header">
    <FaBox className="v_od-card-icon" />
    <h3>Order Items</h3>
    <span className="v_od-item-count">{order.order_items.length} items</span>
  </div>
  <div className="v_od-card-body">
    {/* Desktop: Table without wrapper */}
    {/* Mobile: Table with scrolling wrapper */}
    <div className="v_od-table-container">
      <table className="v_od-items-table">
        <thead>
          <tr>
            <th>Product</th>
            <th>Quantity</th>
            <th>Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {order.order_items.map((item, idx) => {
            const itemCurrency = item.currency_symbol || currency;
            const itemTotal = item.quantity * parseFloat(item.price);
            return (
              <tr key={idx} className="v_od-item-row">
                <td className="v_od-product-cell">
                  {item.product_image ? (
                    <SafeImage
                      src={JSON.parse(item.product_image)[0]}
                      alt={item.product_name}
                      className="v_od-product-img"
                      onError={(e) => { e.target.src = '/placeholder-product.png'; }}
                    />
                  ) : (
                    <div className="v_od-product-placeholder">
                      <FaBox />
                    </div>
                  )}
                  <span className="v_od-product-name">{item.product_name}</span>
                </td>
                <td className="v_od-quantity-cell">
                  <span className="v_od-quantity-badge">{item.quantity}</span>
                </td>
                <td className="v_od-price-cell">
                  {itemCurrency} {parseFloat(item.price).toFixed(2)}
                </td>
                <td className="v_od-total-cell">
                  <span className="v_od-item-total">
                    {itemCurrency} {itemTotal.toFixed(2)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
</div>
        </div>

        {/* Right Column */}
        <div className="v_od-right-column">
          {/* OTP Verification - Only for ongoing orders */}
          {shouldShowOTP && (
            <div className="v_od-card v_od-otp-card">
              <div className="v_od-card-header">
                <FaShieldAlt className="v_od-card-icon" />
                <h3>Driver PIN Verification</h3>
              </div>
              <div className="v_od-card-body">
                <p className="v_od-otp-instruction">
                  <FaKey className="v_od-otp-icon" />
                  Ask driver for the 6-digit PIN to verify delivery
                </p>
                
                <div className="v_od-otp-input-group">
                  <input
                    type="text"
                    placeholder="Enter 6-digit PIN"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="v_od-otp-input"
                  />
                  <button 
                    onClick={handleVerifyOtp} 
                    className="v_od-otp-btn"
                    disabled={isVerifying || otp.length !== 6}
                  >
                    {isVerifying ? (
                      <>
                        <FaSpinner className="v_od-otp-spinner" />
                        Verifying...
                      </>
                    ) : (
                      'Verify PIN'
                    )}
                  </button>
                </div>

                {otpStatus && (
                  <p className={`v_od-otp-status v_od-otp-status-${otpStatus.status}`}>
                    {otpStatus.status === 'success' ? (
                      <FaCheckCircle className="v_od-status-icon" />
                    ) : (
                      <FaExclamationTriangle className="v_od-status-icon" />
                    )}
                    {otpStatus.message}
                  </p>
                )}

                <div className="v_od-otp-note">
                  <p>⚠️ Only verify when driver arrives with your products</p>
                </div>
              </div>
            </div>
          )}

          {/* OTP Success */}
          {otpStatus?.status === 'success' && (
            <div className="v_od-card v_od-otp-verified">
              <div className="v_od-card-header">
                <FaCheckCircle className="v_od-card-icon v_od-success-icon" />
                <h3> Your Order Completed Successful </h3>
              </div>
              <div className="v_od-card-body">
                <div className="v_od-success-content">
                  <FaCheckCircle className="v_od-success-check" />
                  <p className="v_od-success-message">Driver Verified Successfully ✅</p>
                  <p className="v_od-success-time">
                    Verified at {order.otp_updated_at
                      ? new Date(order.otp_updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Order Summary */}
          <div className="v_od-card v_od-summary-card">
            <div className="v_od-card-header">
              <FaReceipt className="v_od-card-icon" />
              <h3>Order Summary</h3>
            </div>
            <div className="v_od-card-body">
              <div className="v_od-summary-row">
                <span>Subtotal</span>
                <span>{currency} {parseFloat(order.subtotal_amount || 0).toFixed(2)}</span>
              </div>

              <div className="v_od-summary-row">
                <span>Tax</span>
                <span>{currency} {parseFloat(order.tax_amount || 0).toFixed(2)}</span>
              </div>
              <div className="v_od-summary-divider"></div>
              <div className="v_od-summary-row v_od-total-row">
                <span>Total Amount</span>
                <span className="v_od-total-amount">
                  {currency} {parseFloat(order.total_amount || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
